import { useState, useMemo, useEffect } from 'react'
import { useGetFubUsers } from '@forwardflow/queries/onboarding.queries'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { TFubUser } from '@forwardflow/types/onboarding'

export const useTeamInvitation = () => {
   const { onboardingData, setInvitedUsers } = useOnboardingStoreV2()
   const { pricingData, email: userEmail } = onboardingData

   // Fetch FUB users
   const { data: fubUsersData } = useGetFubUsers(
      onboardingData?.organizationId ?? '',
      1000,
      1,
      onboardingData?.isFUBConnected
   )
   const fubUsers = useMemo(() => fubUsersData?.data || [], [fubUsersData])

   // Local state
   const [fubUser, setFubUser] = useState<Partial<TFubUser> | null>(
      onboardingData?.invitedUsers?.find((u) => u.isOwner) ?? null
   )
   const [invitedTeamMembers, setInvitedTeamMembers] = useState<TFubUser[]>(
      onboardingData?.invitedUsers?.filter((u) => !u.isOwner) || []
   )
   const [editOwnerUser, setEditOwnerUser] = useState<boolean>(false)

   // Computed values
   const maxTeamMembers = (pricingData?.seatsCount ?? 1) - 1

   const foundAccount = useMemo(
      () => fubUsers?.find((u) => u.email === userEmail),
      [fubUsers, userEmail]
   )

   const userData = useMemo(() => {
      return fubUsers
         ?.map((user: TFubUser) => ({
            value: String(user.id),
            label: `${user.firstName} ${user.lastName} - ${user.email}`,
         }))
         .filter((u) => String(u.value) !== String(fubUser?.id))
   }, [fubUsers, fubUser?.id])

   const fubOwnerUserData = useMemo(() => {
      return fubUsers
         ?.map((user: TFubUser) => ({
            value: String(user.id),
            label: `${user.firstName} ${user.lastName} - ${user.email}`,
         }))
         .filter(
            (u) =>
               !invitedTeamMembers?.some(
                  (i) => String(i.id) === String(u.value)
               )
         )
   }, [fubUsers, invitedTeamMembers])

   const shouldDisableInvite = (invitedTeamMembers?.length ?? 0) >= maxTeamMembers

   // Handlers
   const handleInviteUser = (selectedIds: string[]) => {
      const newMembers = selectedIds
         .map((id) => fubUsers?.find((u: TFubUser) => String(u.id) === id))
         .filter(Boolean) as TFubUser[]
      setInvitedTeamMembers(newMembers)
   }

   const handleRemoveUser = (userId: string) => {
      setInvitedTeamMembers(
         invitedTeamMembers?.filter(
            (user) => String(user.id) !== String(userId)
         )
      )
   }

   const handleSelectOwner = (value: string | null) => {
      const selected =
         fubUsers?.find((u) => String(u.id) === String(value)) || null
      setFubUser(selected)
      setEditOwnerUser(false)
      if (selected) {
         setInvitedTeamMembers(
            invitedTeamMembers?.filter(
               (u) => String(u.id) !== String(selected.id)
            )
         )
      }
   }

   const handleSaveAndContinue = () => {
      setInvitedUsers([
         ...invitedTeamMembers,
         ...(fubUser?.id ? [{ ...fubUser, isOwner: true } as TFubUser] : []),
      ])
   }

   // Auto-select found account
   useEffect(() => {
      if (foundAccount && !fubUser) {
         setEditOwnerUser(true)
         setFubUser(foundAccount)
      }
   }, [foundAccount, fubUser])

   // Auto-update phone number if empty in fubUser but exists in fubUsers
   useEffect(() => {
      if (fubUser?.id && !fubUser?.phone && fubUsers.length > 0) {
         const userWithPhone = fubUsers.find(
            (u) => String(u.id) === String(fubUser.id) && u.phone
         )
         
         if (userWithPhone?.phone) {
            const updatedFubUser = {
               ...fubUser,
               phone: userWithPhone.phone,
            }
            
            // Update local state
            setFubUser(updatedFubUser)
            
            // Update zustand store
            const currentInvitedUsers = onboardingData?.invitedUsers || []
            const updatedInvitedUsers = currentInvitedUsers.map((user) => {
               if (String(user.id) === String(fubUser.id) && user.isOwner) {
                  return {
                     ...user,
                     phone: userWithPhone.phone,
                  } as TFubUser
               }
               return user
            })
            
            // Only update if the owner user exists in invitedUsers
            const ownerExists = currentInvitedUsers.some(
               (u) => String(u.id) === String(fubUser.id) && u.isOwner
            )
            
            if (ownerExists) {
               setInvitedUsers(updatedInvitedUsers)
            }
         }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [fubUser?.id, fubUser?.phone, fubUsers])

   return {
      // State
      fubUser,
      invitedTeamMembers,
      editOwnerUser,
      // Computed
      maxTeamMembers,
      foundAccount,
      userData,
      fubOwnerUserData,
      shouldDisableInvite,
      isOnboardingCompleted: onboardingData?.isOnBoardingCompleted,
      // Setters
      setFubUser,
      setEditOwnerUser,
      // Handlers
      handleInviteUser,
      handleRemoveUser,
      handleSelectOwner,
      handleSaveAndContinue,
   }
}

